import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BookmarkPlus, ChevronDown, Loader2, Sparkles, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared cache so every picker in the dialog reflects a save immediately
let cache = null;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn(cache));

async function loadSnippets(force = false) {
  if (cache && !force) return cache;
  const res = await axios.get(`${API}/cms485/snippets`);
  cache = res.data || [];
  notify();
  return cache;
}

export function SnippetPicker({ field, label, value, onInsert }) {
  const [all, setAll] = useState(cache || []);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [showSave, setShowSave] = useState(false);

  useEffect(() => {
    listeners.add(setAll);
    loadSnippets().catch(() => {});
    return () => listeners.delete(setAll);
  }, []);

  const items = all.filter((s) => s.field === field);
  const slug = field.replace(/_/g, "-");

  const insert = (text) => {
    const cur = (value || "").trim();
    onInsert(cur ? `${cur}\n${text}` : text);
    setOpen(false);
    toast.success(`Inserted "${label}" snippet`);
  };

  const save = async () => {
    if (!title.trim() || !(value || "").trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/cms485/snippets`, { field, title: title.trim(), text: value.trim() });
      await loadSnippets(true);
      setTitle("");
      setShowSave(false);
      toast.success("Snippet saved for your agency");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save snippet");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    try {
      await axios.delete(`${API}/cms485/snippets/${s.id}`);
      await loadSnippets(true);
      toast.success("Snippet removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove snippet");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs gap-1" data-testid={`snippets-${slug}-btn`}>
          <Sparkles className="w-3.5 h-3.5" /> Snippets{items.length ? ` (${items.length})` : ""} <ChevronDown className="w-3 h-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid={`snippets-${slug}-popover`}>
        <div className="px-3 py-2 border-b text-xs font-medium text-slate-600">{label} snippets · tap to insert</div>
        <div className="max-h-64 overflow-y-auto">
          {items.length === 0 && (
            <p className="px-3 py-6 text-xs text-slate-400 text-center">No saved snippets yet. Type your usual wording below, then “Save current text”.</p>
          )}
          {items.map((s) => (
            <div key={s.id} className="group flex items-start gap-2 px-3 py-2 hover:bg-slate-50 border-b last:border-b-0">
              <button type="button" className="flex-1 text-left" onClick={() => insert(s.text)} data-testid={`snippet-item-${s.id}`}>
                <p className="text-sm font-medium text-slate-800">{s.title}</p>
                <p className="text-xs text-slate-500 line-clamp-2">{s.text}</p>
              </button>
              <button type="button" className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600 mt-1" onClick={() => remove(s)} title="Remove" data-testid={`snippet-delete-${s.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        <div className="border-t p-2">
          {showSave ? (
            <div className="flex gap-2">
              <Input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Name this snippet (e.g., Diabetic MOTs)" className="h-8 text-sm" onKeyDown={(e) => e.key === "Enter" && save()} data-testid={`snippet-title-${slug}`} />
              <Button type="button" size="sm" className="h-8" onClick={save} disabled={saving || !title.trim()} data-testid={`snippet-save-${slug}`}>
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
              </Button>
            </div>
          ) : (
            <Button type="button" variant="ghost" size="sm" className="w-full h-8 text-xs justify-start gap-1" disabled={!(value || "").trim()} onClick={() => setShowSave(true)} data-testid={`snippet-save-current-${slug}`}>
              <BookmarkPlus className="w-3.5 h-3.5" /> Save current text as a snippet
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
